import { splitTextWithUrls } from '@griply/utils';
import Typography from '@mui/material/Typography';
import React, { useMemo } from 'react';

import * as Styled from './url-parser.style';

export type LinkBaseProps = {
  href?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  children: React.ReactNode;
  target?: string;
  rel?: string;
};

export type LinkedTextProps = {
  value: string;
  linkComponent?: React.ComponentType<LinkBaseProps>;
};

// a link click must not reach a click-to-edit surface underneath
const stopPropagation = (e: React.MouseEvent<HTMLAnchorElement>) =>
  e.stopPropagation();

// The linkified segments with no wrapper element, so they compose inside rich-text marks
// (bold, list items) as well as inside UrlParser's own Typography.
export const LinkedText: React.FC<LinkedTextProps> = ({
  value,
  linkComponent: Link = Styled.ParsedLink,
}) => {
  const elements = useMemo(() => {
    const segments = splitTextWithUrls(value);

    if (!segments.length) {
      return [];
    }

    const hasAnyUrl = segments.some((segment) => segment.kind === 'url');
    if (!hasAnyUrl) {
      return [value];
    }

    return segments.map((segment, index) => {
      if (segment.kind === 'text') {
        return segment.value;
      }
      return (
        <Link
          key={`url-${index}`}
          href={segment.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={stopPropagation}
        >
          {segment.value}
        </Link>
      );
    });
  }, [value, Link]);

  return <>{elements.length ? elements : value}</>;
};

export type UrlParserProps = Omit<
  React.ComponentProps<typeof Typography>,
  'children'
> & {
  value: string;
  linkComponent?: React.ComponentType<LinkBaseProps>;
  respectNewLines?: boolean;
  explicitTextCursor?: boolean;
  hasPaddingRight?: boolean;
};

export const UrlParser: React.FC<UrlParserProps> = ({
  value,
  linkComponent,
  respectNewLines,
  explicitTextCursor,
  hasPaddingRight,
  ...rest
}) => (
  <Styled.UrlParser
    $explicitTextCursor={!!explicitTextCursor}
    $hasPaddingRight={!!hasPaddingRight}
    $respectNewLines={!!respectNewLines}
    {...rest}
  >
    <LinkedText value={value} linkComponent={linkComponent} />
  </Styled.UrlParser>
);
